import { useEffect, useMemo, useRef, useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const STEP_ICONS = ['ri-radar-line', 'ri-sparkling-2-line', 'ri-magic-line', 'ri-check-double-line'] as const;
const ADVANCE_MS = 5000;
/** One command centre screenshot per step, same order as `home.exceptionStory.steps`. */
const STEP_IMAGES = [
  '/images/home/command-centre-step-1.jpg',
  '/images/home/command-centre-step-2.jpg',
  '/images/home/command-centre-step-3.jpg',
  '/images/home/command-centre-step-4.jpg',
] as const;

/** Area of each screenshot the spotlight outlines, in % of the image (left, top, width, height). */
const STEP_SPOTLIGHT = [
  { left: 64, top: 31, width: 33.6, height: 49 }, // exceptions queue
  { left: 67.5, top: 50, width: 28, height: 30 }, // Copilot's answer
  { left: 68, top: 50, width: 28.5, height: 37.5 }, // suggested fix
  { left: 64.8, top: 39, width: 31.8, height: 49 }, // confirmation + new ETAs
] as const;
/** Centre of the "Reassign 2 stops" button on the step 3 screenshot, in %. */
const REASSIGN_BUTTON = { left: 89.1, top: 83.5 } as const;
const SUGGEST_STEP = 2;

type Step = { title: string; body: string };
type Annotation = { status: string; detail: string };
type Pillar = { kicker: string; title: string; subtitle: string; cards: { title: string; line: string }[] };

/** Command centre, then Copilot — same order as `home.howItWorksBlocks.majorFeatures`. */
const PILLAR_STYLE = [
  {
    icon: 'ri-dashboard-3-line',
    chip: 'bg-emerald-50 text-emerald-700',
    cards: ['ri-map-2-line', 'ri-plug-line', 'ri-flashlight-line', 'ri-file-list-3-line'],
    href: '/how-it-works',
    linkKey: 'home.lifecycle.cta',
  },
  {
    icon: 'ri-sparkling-2-line',
    chip: 'bg-blue-50 text-blue-700',
    cards: ['ri-radar-line', 'ri-magic-line', 'ri-shield-check-line', 'ri-question-answer-line'],
    href: '/features/ai-agents',
    linkKey: 'home.exceptionStory.cta',
  },
] as const;

function readPillars(t: TFunction): Pillar[] {
  const raw = t('home.howItWorksBlocks.majorFeatures', { returnObjects: true });
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((p): p is Pillar => typeof p === 'object' && p !== null && typeof (p as Pillar).title === 'string' && Array.isArray((p as Pillar).cards))
    .map((p) => ({ ...p, cards: p.cards.filter((c) => typeof c?.title === 'string' && typeof c?.line === 'string') }));
}

/** A late-driver exception resolved end to end — the product story in four beats, one product screenshot per beat. */
type Props = {
  /** The Command centre / Copilot cards under the steps; the AI agents page hides them. */
  showPillars?: boolean;
};

export default function ExceptionStory({ showPillars = true }: Props) {
  const { t, i18n } = useTranslation();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  const steps = useMemo(() => {
    const raw = t('home.exceptionStory.steps', { returnObjects: true });
    if (!Array.isArray(raw)) return [];
    return raw.filter(
      (s): s is Step => typeof s === 'object' && s !== null && typeof (s as Step).title === 'string' && typeof (s as Step).body === 'string'
    );
  }, [t, i18n.language]);

  const pillars = useMemo(() => readPillars(t), [t, i18n.language]);
  const annotations = useMemo(() => {
    const raw = t('home.exceptionStory.annotations', { returnObjects: true });
    if (!Array.isArray(raw)) return [];
    return raw.filter(
      (o): o is Annotation => typeof o === 'object' && o !== null && typeof (o as Annotation).status === 'string' && typeof (o as Annotation).detail === 'string'
    );
  }, [t, i18n.language]);

  // Start the story (and the reveal) only once the section is on screen.
  const sectionRef = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (inView) return;
    const check = () => {
      const el = sectionRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.8 && rect.bottom > 0) setInView(true);
    };
    check();
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    return () => {
      window.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
    };
  }, [inView]);

  const reduceMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    if (!inView || paused || reduceMotion || steps.length === 0) return;
    const id = window.setTimeout(() => setActive((i) => (i + 1) % steps.length), ADVANCE_MS);
    return () => window.clearTimeout(id);
  }, [active, inView, paused, reduceMotion, steps.length]);

  const shown = inView || reduceMotion;
  const reveal = (i: number, base = 0) => ({
    className: `transition-all duration-700 ease-out ${shown ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'}`,
    style: { transitionDelay: shown && !reduceMotion ? `${base + i * 120}ms` : '0ms' },
  });
  const note = annotations[active];

  return (
    <section ref={sectionRef} className="relative overflow-hidden border-t border-gray-100 bg-gradient-to-b from-slate-50 via-blue-50/40 to-white py-20 lg:py-28" aria-labelledby="exception-story-title">
      <div className="pointer-events-none absolute -right-32 top-1/4 h-96 w-96 rounded-full bg-blue-400/15 blur-3xl" aria-hidden />
      <div className="pointer-events-none absolute -left-32 bottom-0 h-80 w-80 rounded-full bg-emerald-400/10 blur-3xl" aria-hidden />

      <div className="relative mx-auto max-w-7xl px-6">
        <div className="mx-auto mb-12 max-w-2xl text-center lg:mb-16">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-700">{t('home.exceptionStory.eyebrow')}</p>
          <h2 id="exception-story-title" className="mt-3 text-3xl font-bold tracking-tight text-gray-900 lg:text-4xl">
            {t('home.exceptionStory.title')}
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-gray-600">{t('home.exceptionStory.subtitle')}</p>
        </div>

        <div
          className="grid items-center gap-10 lg:grid-cols-12 lg:gap-14"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <ol className="m-0 list-none space-y-3 p-0 lg:col-span-5">
            {steps.map((step, i) => {
              const isActive = i === active;
              const r = reveal(i, 150);
              return (
                <li key={step.title} className={r.className} style={r.style}>
                  <button
                    type="button"
                    onClick={() => setActive(i)}
                    aria-current={isActive ? 'step' : undefined}
                    className={`relative flex w-full gap-4 overflow-hidden rounded-2xl border p-4 text-start transition-colors sm:p-5 ${
                      isActive ? 'border-blue-500 bg-white shadow-lg shadow-blue-900/5 ring-1 ring-blue-500/20' : 'border-gray-200 bg-white/60 hover:border-gray-300 hover:bg-white'
                    }`}
                  >
                    <span
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors ${
                        isActive ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      <i className={`${STEP_ICONS[i] ?? 'ri-checkbox-circle-line'} text-lg`} aria-hidden />
                    </span>
                    <span>
                      <span className={`block font-semibold ${isActive ? 'text-gray-900' : 'text-gray-700'}`}>
                        {i + 1}. {step.title}
                      </span>
                      <span className={`mt-1 block text-sm leading-relaxed ${isActive ? 'text-gray-600' : 'text-gray-500'}`}>
                        {step.body}
                      </span>
                    </span>
                    {isActive && !paused && !reduceMotion && (
                      <span
                        key={active}
                        className="absolute bottom-0 start-0 h-0.5 bg-blue-500"
                        style={{ animation: `story-progress ${ADVANCE_MS}ms linear forwards` }}
                        aria-hidden
                      />
                    )}
                  </button>
                </li>
              );
            })}
          </ol>

          <div className="order-first lg:order-none lg:col-span-7">
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl shadow-blue-900/10">
              <div className="flex items-center gap-3 border-b border-gray-200 bg-gray-50 px-4 py-2.5">
                <span className="flex gap-1.5" aria-hidden>
                  <span className="h-2.5 w-2.5 rounded-full bg-red-300" />
                  <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-300" />
                </span>
                <span className="mx-auto flex items-center gap-1.5 rounded-md bg-white px-3 py-1 text-[11px] text-gray-500 ring-1 ring-gray-200">
                  <i className="ri-lock-2-fill text-[10px] text-gray-400" aria-hidden />
                  app.geofleet.ai
                </span>
                <span className="w-[42px]" aria-hidden />
              </div>
              <div className="relative aspect-[16/10] bg-slate-50" aria-live="polite">
                {STEP_IMAGES.map((src, i) => (
                  <img
                    key={src}
                    src={src}
                    alt={i === active ? steps[i]?.body ?? '' : ''}
                    aria-hidden={i !== active}
                    loading={i === 0 ? 'eager' : 'lazy'}
                    className={`absolute inset-0 h-full w-full object-cover object-left-top transition-opacity duration-500 ${
                      i === active ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                ))}
                {/* Spotlight: dims the screen and glides to the part that matters in this step */}
                {inView && !reduceMotion && STEP_SPOTLIGHT[active] && (
                  <div
                    className="pointer-events-none absolute z-[5] rounded-xl ring-2 ring-blue-500 transition-all duration-700 ease-in-out"
                    style={{
                      left: `${STEP_SPOTLIGHT[active].left}%`,
                      top: `${STEP_SPOTLIGHT[active].top}%`,
                      width: `${STEP_SPOTLIGHT[active].width}%`,
                      height: `${STEP_SPOTLIGHT[active].height}%`,
                      boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.28), 0 0 24px 4px rgba(37, 99, 235, 0.35)',
                    }}
                    aria-hidden
                  />
                )}
                {/* Step 3: a cursor moves to "Reassign 2 stops" and clicks it */}
                {inView && !reduceMotion && active === SUGGEST_STEP && (
                  <div key={`cursor-${active}`} className="pointer-events-none absolute inset-0 z-[6]" aria-hidden>
                    <span
                      className="absolute h-10 w-10 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-500/40"
                      style={{
                        left: `${REASSIGN_BUTTON.left}%`,
                        top: `${REASSIGN_BUTTON.top}%`,
                        animation: `story-click ${ADVANCE_MS}ms ease-out forwards`,
                      }}
                    />
                    <i
                      className="ri-cursor-fill absolute text-2xl text-gray-900 drop-shadow-[0_2px_2px_rgba(255,255,255,0.9)]"
                      style={{ animation: `story-cursor ${ADVANCE_MS}ms ease-in-out forwards` }}
                    />
                  </div>
                )}
                {note && (
                  <>
                    <div
                      key={`status-${active}`}
                      className="absolute start-2 top-2 z-10 inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white/95 px-2.5 py-1 text-[11px] sm:start-4 sm:top-4 sm:px-3 sm:py-1.5 sm:text-xs font-semibold text-gray-900 shadow-lg backdrop-blur opacity-0 animate-fade-in-up sm:text-sm"
                    >
                      <span className="relative flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-60" />
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-600" />
                      </span>
                      {note.status}
                    </div>
                    <div
                      key={`detail-${active}`}
                      className="absolute bottom-4 start-4 z-10 hidden items-center gap-2 rounded-xl sm:flex border border-gray-200 bg-white/95 px-3.5 py-2 text-xs text-gray-700 shadow-xl backdrop-blur opacity-0 animate-fade-in-up animation-delay-200 sm:text-sm"
                    >
                      <i className={`${STEP_ICONS[active] ?? 'ri-checkbox-circle-line'} text-base text-blue-600`} aria-hidden />
                      {note.detail}
                    </div>
                  </>
                )}
              </div>
              <p className="border-t border-gray-200 bg-white px-4 py-2 text-end text-[10px] uppercase tracking-wider text-gray-400">
                {t('home.exceptionStory.mock.sample')}
              </p>
            </div>
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
              <p className="flex items-center gap-2 text-sm text-gray-600">
                <i className="ri-shield-check-line text-emerald-600" aria-hidden />
                {t('home.exceptionStory.footnote')}
              </p>
              <Link
                to="/features/ai-agents"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:text-blue-600"
              >
                {t('home.exceptionStory.cta')}
                <i className="ri-arrow-right-line rtl:rotate-180" aria-hidden />
              </Link>
            </div>
          </div>
        </div>
      </div>
      {showPillars && pillars.length > 0 && (
        <div className="relative mx-auto mt-16 max-w-7xl px-6 lg:mt-20">
          <div className="grid gap-5 border-t border-gray-200 pt-12 lg:grid-cols-2">
            {pillars.map((pillar, pi) => {
              const style = PILLAR_STYLE[pi] ?? PILLAR_STYLE[0];
              const r = reveal(pi, 700);
              return (
                <article
                  key={pillar.title}
                  className={`flex flex-col rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-lg sm:p-7 ${r.className}`}
                  style={r.style}
                >
                  <div className="flex items-center gap-3">
                    <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${style.chip}`}>
                      <i className={`${style.icon} text-lg`} aria-hidden />
                    </span>
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gray-500">{pillar.kicker}</p>
                      <h3 className="text-xl font-bold text-gray-900">{pillar.title}</h3>
                    </div>
                  </div>
                  <p className="mt-4 text-[15px] leading-relaxed text-gray-600">{pillar.subtitle}</p>
                  <ul className="m-0 mt-5 grid list-none grid-cols-1 gap-2 p-0 sm:grid-cols-2">
                    {pillar.cards.map((card, ci) => (
                      <li
                        key={card.title}
                        title={card.line}
                        className="flex items-center gap-2.5 rounded-xl border border-gray-100 bg-slate-50 px-3 py-2.5 text-sm font-semibold text-gray-800"
                      >
                        <i className={`${style.cards[ci] ?? 'ri-checkbox-circle-line'} text-base text-blue-600`} aria-hidden />
                        {card.title}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={style.href}
                    className="mt-auto inline-flex items-center gap-1.5 pt-6 text-sm font-semibold text-blue-700 hover:text-blue-600"
                  >
                    {t(style.linkKey)}
                    <i className="ri-arrow-right-line rtl:rotate-180" aria-hidden />
                  </Link>
                </article>
              );
            })}
          </div>
        </div>
      )}
      <style>
        {`@keyframes story-progress { from { width: 0 } to { width: 100% } }
@keyframes story-cursor {
  0%, 15% { left: 55%; top: 70%; opacity: 0; }
  25% { opacity: 1; }
  55% { left: ${REASSIGN_BUTTON.left}%; top: ${REASSIGN_BUTTON.top}%; transform: scale(1); }
  60% { left: ${REASSIGN_BUTTON.left}%; top: ${REASSIGN_BUTTON.top}%; transform: scale(0.85); }
  66%, 100% { left: ${REASSIGN_BUTTON.left}%; top: ${REASSIGN_BUTTON.top}%; transform: scale(1); opacity: 1; }
}
@keyframes story-click {
  0%, 58% { transform: translate(-50%, -50%) scale(0); opacity: 0; }
  60% { opacity: 1; }
  80%, 100% { transform: translate(-50%, -50%) scale(2.4); opacity: 0; }
}`}
      </style>
    </section>
  );
}
